import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Server, CheckCircle2, Globe, FileText, Users, RefreshCw, Bell, Building2, ExternalLink } from 'lucide-react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const Section = ({ icon: Icon, title, children }) => (
  <section>
    <h2 className="text-xl md:text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
      <Icon className="h-5 w-5 text-[#1e3a5f] shrink-0" />
      {title}
    </h2>
    {children}
  </section>
);

const TrustCenter = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="trust-center-page">
      <SEO title="Trust Center | StayMii" description="StayMii Trust Center - Transparency on security, compliance, privacy protection, and system reliability." canonicalUrl="/trust" />
      <LandingNavBar />

      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] pt-28 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="h-10 w-10 text-white mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" data-testid="trust-title">Trust Center</h1>
          <p className="text-white/70 text-sm max-w-2xl mx-auto">Transparency on how StayMii protects customer data, maintains platform security, and ensures regulatory compliance.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8 space-y-8">

          <Section icon={Globe} title="Platform Overview">
            <div className="bg-slate-50 rounded-lg p-4 text-slate-600 text-sm mb-4">
              <p className="font-semibold text-slate-800">Operated by Mii Global Partner Holdings Sdn Bhd</p>
              <p>Registration No: 202601028203</p>
              <p>11, Taman Bukit Dumbar, 11600 Jelutong, Penang, Malaysia</p>
            </div>
            <p className="text-slate-600 leading-relaxed mb-4">StayMii provides hospitality technology solutions through three core modules:</p>
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">StayMii Lite</h3>
                <p className="text-slate-600 text-sm mb-2">A lightweight booking and record management platform for operators without an existing PMS or channel manager. Supports:</p>
                <ul className="list-disc list-inside text-slate-600 text-sm space-y-1 ml-2">
                  <li>Reservation and booking management</li>
                  <li>Guest profile management</li>
                  <li>Billing and folio operations</li>
                  <li>Housekeeping coordination</li>
                  <li>OTA integration and operational reporting</li>
                </ul>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">StayMii e-Invoice</h3>
                <p className="text-slate-600 text-sm">Supports digital invoicing and helps businesses comply with Malaysia's electronic invoicing requirements administered by LHDN through the MyInvois platform.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2">StayMii e-Ticket</h3>
                <p className="text-slate-600 text-sm">A ticketing and OTA distribution module for attractions, tours, cultural venues, and event operators — including QR-coded electronic ticket issuance, integration with connected OTA distribution channels, on-site scanner validation, and OTA payout reconciliation.</p>
              </div>
            </div>
          </Section>

          <Section icon={Lock} title="Security Principles">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii is built with security as a core platform principle:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Secure data transmission using HTTPS and encryption protocols',
                'Role-based access control for system users',
                'System monitoring and activity logging',
                'Infrastructure protection against unauthorized access'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 bg-slate-50 rounded-lg p-3">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={Eye} title="Data Protection">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii is designed to support compliance with Malaysia's Personal Data Protection Act (PDPA). We implement responsible data handling practices including:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Secure storage of guest and reservation information</li>
              <li>Controlled access to sensitive operational data</li>
              <li>Protection of financial and invoice records</li>
            </ul>
            <p className="text-slate-600 mt-3 text-sm">Businesses using StayMii remain responsible for managing the personal data they collect from guests.</p>
          </Section>

          <Section icon={FileText} title="Regulatory Compliance">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-3">
              <h3 className="font-semibold text-blue-800 mb-2">LHDN e-Invoice Framework</h3>
              <p className="text-blue-700 text-sm mb-2">StayMii e-Invoice assists businesses in generating electronic invoices compatible with the MyInvois system operated by LHDN. Tools include:</p>
              <ul className="list-disc list-inside text-blue-700 text-sm space-y-1 ml-2">
                <li>Electronic invoice preparation</li>
                <li>Digital invoice record management</li>
                <li>Integration between hospitality billing and invoicing workflows</li>
              </ul>
            </div>
            <p className="text-slate-600 text-sm">Businesses remain responsible for ensuring compliance with tax regulations.</p>
          </Section>

          <Section icon={Server} title="Platform Reliability">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii operates on secure cloud infrastructure designed for reliability and scalability:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Redundant server architecture',
                'System monitoring',
                'Data backups',
                'Infrastructure-level security controls'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 bg-slate-50 rounded-lg p-3">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={RefreshCw} title="Third-Party Integrations">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii integrates with external hospitality platforms to support hotel distribution and operations:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Online travel agencies such as Booking.com and Agoda</li>
              <li>Channel management systems</li>
              <li>Accounting and finance software</li>
              <li>Payment gateway providers</li>
            </ul>
            <p className="text-slate-600 mt-2 text-sm">Third-party services operate independently and may have their own security and compliance policies.</p>
          </Section>

          <Section icon={Users} title="Responsible Platform Use">
            <p className="text-slate-600 leading-relaxed mb-3">Customers using StayMii are responsible for:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 ml-2">
              <li>Managing authorized user access</li>
              <li>Protecting login credentials</li>
              <li>Ensuring accurate guest and invoice information</li>
              <li>Complying with applicable hospitality and tax regulations</li>
            </ul>
          </Section>

          <Section icon={Shield} title="Independent Security & Trust Reports">
            <p className="text-slate-600 leading-relaxed mb-4">
              StayMii's safety, security and reputation status from independent third-party services. Every link below opens the verifier's official report for <code className="bg-slate-100 px-1 rounded text-xs">staymii.com</code> — you can confirm our standing without trusting our word.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { label: 'Norton Safe Web status', sub: 'symantec.com', href: 'https://safeweb.norton.com/report/show?url=staymii.com' },
                { label: 'Scamadviser profile', sub: 'scamadviser.com', href: 'https://www.scamadviser.com/check-website/staymii.com' },
                { label: 'Google Safe Browsing status', sub: 'transparencyreport.google.com', href: 'https://transparencyreport.google.com/safe-browsing/search?url=staymii.com' },
                { label: 'SSL Labs report', sub: 'ssllabs.com', href: 'https://www.ssllabs.com/ssltest/analyze.html?d=staymii.com&hideResults=on' },
                { label: 'Security headers report', sub: 'securityheaders.com', href: 'https://securityheaders.com/?q=staymii.com&followRedirects=on' },
                { label: 'SSM registration record', sub: '202601028203', href: 'https://www.ssm.com.my/Pages/Search-Information/EzBiz-Online-Services.aspx' },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center justify-between gap-3 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 rounded-lg p-3 transition-colors group"
                  data-testid={`trust-external-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <div className="min-w-0">
                    <div className="text-slate-800 text-sm font-medium truncate">{item.label}</div>
                    <div className="text-slate-500 text-xs truncate">{item.sub}</div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-[#2563EB] shrink-0" />
                </a>
              ))}
            </div>
            <p className="text-slate-500 mt-4 text-xs">
              These reports are operated independently of StayMii. We don't control the content shown on those third-party sites — we link to them so you can audit our standing yourself.
            </p>
          </Section>

          <Section icon={Bell} title="Transparency">
            <p className="text-slate-600 leading-relaxed mb-3">StayMii is committed to transparency regarding its policies and security practices. Please refer to:</p>
            <div className="space-y-2">
              <Link to="/terms-of-service" className="block bg-slate-50 rounded-lg p-3 text-[#2563EB] text-sm font-medium hover:bg-blue-50 transition-colors">Terms of Service</Link>
              <Link to="/privacy-policy" className="block bg-slate-50 rounded-lg p-3 text-[#2563EB] text-sm font-medium hover:bg-blue-50 transition-colors">Privacy Policy</Link>
              <Link to="/security" className="block bg-slate-50 rounded-lg p-3 text-[#2563EB] text-sm font-medium hover:bg-blue-50 transition-colors">Security & Compliance</Link>
              <Link to="/cookies" className="block bg-slate-50 rounded-lg p-3 text-[#2563EB] text-sm font-medium hover:bg-blue-50 transition-colors">Cookies Policy</Link>
            </div>
          </Section>

          <Section icon={Building2} title="Contact">
            <p className="text-slate-600 leading-relaxed mb-3">For security, compliance, or trust-related inquiries:</p>
            <div className="bg-slate-50 rounded-lg p-4 text-slate-600 text-sm">
              <p className="font-semibold text-slate-800">Mii Global Partner Holdings Sdn Bhd</p>
              <p>11, Taman Bukit Dumbar, 11600 Jelutong, Penang, Malaysia</p>
              <p className="mt-2">Email: <a href="mailto:support@staymii.com" className="text-[#2563EB] hover:underline">support@staymii.com</a></p>
              <p>Website: <a href="https://staymii.com" className="text-[#2563EB] hover:underline">https://staymii.com</a></p>
            </div>
          </Section>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default TrustCenter;
